import { Link, useLocation } from 'react-router-dom';
import { ArrowUpRight, Headphones, Loader2, Package, CreditCard, KeyRound } from 'lucide-react';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { openPatientSupportChat, SupportChatPlacement, usePatientSupportChat } from '../../lib/patientSupportChat';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import { usePatientAvaStatus } from '../../lib/patientAva';
import { AvaMark } from './AvaMark';

/**
 * Support chat entry points. Support (orders, shipping, payments, the account)
 * is the first place the portal sends a patient for help; the clinic's care
 * team (Messages) is for clinical questions.
 */

function UnreadDot({ count }: { count: number }) {
  if (!count) return null;
  return <>
    <span aria-hidden="true" className="grid h-5 min-w-5 place-items-center rounded-full bg-[var(--portal-accent)] px-1 text-[11px] font-bold leading-none text-[var(--portal-accent-fg)]">{count > 9 ? '9+' : count}</span>
    <span className="sr-only">, {count} unread</span>
  </>;
}

/** "Chat with support". Renders nothing on deploys without support chat. */
export function SupportChatButton({ variant = 'solid', label = 'Chat with support', placement }: { variant?: 'solid' | 'inverse'; label?: string; placement: SupportChatPlacement }) {
  const { status, unread } = usePatientSupportChat();
  if (status === 'unavailable' || status === 'idle') return null;
  const loading = status === 'loading';
  return <button type="button" onClick={() => openPatientSupportChat(placement)} disabled={loading}
    className={`inline-flex h-11 items-center gap-2 rounded-full px-5 text-sm font-semibold transition focus:outline-none focus-visible:ring-4 focus-visible:ring-[rgba(224,86,58,0.25)] disabled:opacity-70 ${variant === 'inverse' ? 'bg-white text-[#241a15] hover:bg-white/90' : 'bg-[var(--portal-primary)] text-[var(--portal-primary-fg)] hover:opacity-90'}`}>
    {loading ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : <Headphones aria-hidden="true" className="h-4 w-4" />}
    {label}<UnreadDot count={unread} />
  </button>;
}

/**
 * Floating "Chat with support" on portal pages, clear of the phone tab bar.
 * Not on the Help page (its own button is the page's main action), or on
 * Messages and Ask Ava (where it would cover the composer).
 */
export function SupportLauncher() {
  const { status, unread } = usePatientSupportChat();
  const impersonated = useDtpAuthStore(s => s.impersonated);
  const { pathname } = useLocation();
  if (status !== 'ready' || impersonated || /\/(support|messages|assistant)(?:\/|$)/.test(pathname)) return null;
  return <button type="button" onClick={() => openPatientSupportChat('launcher')} aria-label={`Chat with support${unread ? `, ${unread} unread` : ''}`}
    className="fixed bottom-[calc(env(safe-area-inset-bottom)+5.5rem)] right-4 z-20 inline-flex h-12 items-center gap-2 rounded-full bg-[var(--portal-primary)] pl-4 pr-5 text-sm font-semibold text-[var(--portal-primary-fg)] shadow-[0_14px_30px_-12px_rgba(36,26,21,0.55)] transition hover:opacity-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-[rgba(224,86,58,0.3)] lg:bottom-6 lg:right-6">
    <Headphones aria-hidden="true" className="h-[18px] w-[18px]" />
    <span className="hidden sm:inline">Chat with support</span><span className="sm:hidden">Support</span>
    {unread > 0 && <span aria-hidden="true" className="grid h-5 min-w-5 place-items-center rounded-full bg-[var(--portal-accent)] px-1 text-[11px] font-bold leading-none text-[var(--portal-accent-fg)]">{unread > 9 ? '9+' : unread}</span>}
  </button>;
}

/** Home: the most visible way to get help, a person first and Ava second. Hidden when neither is available. */
export function SupportHomeCard() {
  const { physicianId } = useDtpParams();
  const { status } = usePatientSupportChat();
  const ava = usePatientAvaStatus();
  const impersonated = useDtpAuthStore(s => s.impersonated);
  const chat = status === 'loading' || status === 'ready';
  if (impersonated || (!chat && !ava.available)) return null;
  return <section aria-labelledby="support-home" className="relative overflow-hidden rounded-[24px] bg-[var(--portal-primary)] p-6 text-[var(--portal-primary-fg)] shadow-[0_18px_40px_-28px_rgba(36,26,21,0.6)] sm:p-7 md:col-span-2">
    <span aria-hidden="true" className="pointer-events-none absolute -bottom-16 -right-10 h-48 w-48 rounded-full bg-white/[0.06]" />
    <div className="relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
      <div className="min-w-0">
        <div className="flex items-center gap-2.5"><Headphones aria-hidden="true" className="h-[18px] w-[18px]" /><h2 id="support-home" className="portal-display text-xl font-semibold text-inherit">Need help? We’re here.</h2></div>
        <p className="mt-2 max-w-xl text-[15px] opacity-90">{chat ? 'Chat with our support team about an order, shipping, a payment or your account.' : 'Ava can answer questions about your orders, refills, results and account.'}</p>
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        <SupportChatButton variant="inverse" placement="home" />
        {ava.available && <Link to={dtpPath(physicianId, '/assistant')} state={{ avaPlacement: 'home' }} className="inline-flex h-11 items-center gap-2 rounded-full bg-white/10 px-5 text-sm font-semibold text-inherit ring-1 ring-white/20 transition hover:bg-white/15"><AvaMark size={22} />Ask Ava</Link>}
      </div>
    </div>
  </section>;
}

/** On Messages: send non-clinical questions to support. */
export function NotMedicalBanner() {
  const { status } = usePatientSupportChat();
  if (status === 'unavailable' || status === 'idle') return null;
  return <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[var(--portal-tint-1)] p-4">
    <p className="min-w-0 text-[15px]"><span className="font-semibold">Not a medical question?</span> <span className="text-[var(--portal-muted)]">Orders, shipping, payments and your account are fastest with support.</span></p>
    <SupportChatButton placement="messages_banner" />
  </div>;
}

/** Self-serve links for the Help page. */
export function SupportSelfServe() {
  const { physicianId } = useDtpParams();
  const links = [
    { to: '/orders', icon: Package, title: 'Track an order', description: 'Status, tracking and buying again' },
    { to: '/billing', icon: CreditCard, title: 'Payments and receipts', description: 'Saved cards and every charge' },
    { to: '/account', icon: KeyRound, title: 'Sign-in and notifications', description: 'Password, passkeys, texts and emails' },
  ];
  return <ul className="grid gap-3 sm:grid-cols-3">
    {links.map(({ to, icon: Icon, title, description }) => <li key={to}>
      <Link to={dtpPath(physicianId, to)} className="portal-card group flex h-full flex-col gap-3 p-5 transition hover:-translate-y-0.5 motion-reduce:hover:translate-y-0">
        <span className="flex items-center justify-between"><span aria-hidden="true" className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--portal-tint-2)]"><Icon className="h-5 w-5" /></span><ArrowUpRight aria-hidden="true" className="h-4 w-4 text-[var(--portal-muted)]" /></span>
        <span><span className="portal-display block font-semibold">{title}</span><span className="mt-0.5 block text-sm text-[var(--portal-muted)]">{description}</span></span>
      </Link>
    </li>)}
  </ul>;
}
